import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FileSignature, AlertCircle, Download, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/shared/LoadingState';
import { PDFPreview } from '../documents/components/PDFPreview';
import { SignatureModal } from './components/SignatureModal';
import { useSignatureEnvelopes } from '../../core/hooks/useSignatureEnvelopes';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../core/hooks/useToast';
import { logError } from '../../lib/utils';

const SigningPageHeader: React.FC<{ step?: string }> = ({ step }) => (
  <header className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
    <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4">
      <img
        src="/SentralGroup-Brand-Horizontal.svg"
        alt="Sentral Group"
        className="h-6 w-auto"
      />
      {step && (
        <p className="text-sm text-muted-foreground">{step}</p>
      )}
    </div>
  </header>
);

interface ClientSignerInfo {
  email: string;
  name?: string;
}

interface EnvelopeInfo {
  envelopeId: string;
  originalPdfUrl: string;
  clientSigner: ClientSignerInfo;
  status: string;
  documentName?: string;
}

export const ClientSigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const {
    validateSigningToken,
    submitClientSignature,
    sendOtp,
    verifyOtp,
    downloadSignedPdf,
    loading,
  } = useSignatureEnvelopes();

  const [envelopeInfo, setEnvelopeInfo] = useState<EnvelopeInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const toast = useToast();
  // validateSigningToken is re-created on every render of the hook, so leaving
  // it in the dependency array re-ran this effect on every state update and
  // hammered the callable in a loop. Validate once per token instead.
  const validatedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid signing link');
      setLoadingInfo(false);
      return;
    }

    if (validatedToken.current === token) return;
    validatedToken.current = token;

    const validateToken = async () => {
      try {
        const info = await validateSigningToken(token);
        if (!info || !info.originalPdfUrl || !info.clientSigner) {
          setError('We could not open a document for this link.');
          return;
        }
        setEnvelopeInfo(info);
      } catch (err: any) {
        logError('signatures', err, { source: 'ClientSigningPage.validateToken' });
        // Signers are outside the company: raw callable errors ("action is
        // required") mean nothing to them and read as a broken product. The
        // detail stays in logError; the page says what they can act on.
        const raw = String(err?.message || '');
        setError(
          /expired|used|revoked/i.test(raw)
            ? 'This signing link has already been used, or it has expired.'
            : 'We could not open a document for this link.'
        );
      } finally {
        setLoadingInfo(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSendOtp = async () => {
    if (!envelopeInfo) return;
    setOtpLoading(true);
    try {
      await sendOtp(envelopeInfo.envelopeId, envelopeInfo.clientSigner.email);
    } catch (err: any) {
      logError('signatures', err, { source: 'ClientSigningPage.handleSendOtp' });
      toast.error(
        "Couldn't send your verification code",
        err.message ||
          'Check the email address with the sender, then request a new code.'
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    if (!envelopeInfo) return;
    setOtpLoading(true);
    try {
      await verifyOtp(envelopeInfo.envelopeId, envelopeInfo.clientSigner.email, code);
      setOtpVerified(true);
    } catch (err: any) {
      logError('signatures', err, { source: 'ClientSigningPage.handleVerifyOtp' });
      throw err;
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmitSignature = async (data: {
    signatureType: 'draw' | 'type';
    signatureImage?: string;
    typedName?: string;
    signingDate: string;
    consentGiven: boolean;
    otpCode?: string;
  }) => {
    if (!token) return;

    setSubmitting(true);
    try {
      await submitClientSignature(
        token,
        data.signatureType,
        data.signatureImage,
        data.typedName,
        data.signingDate,
        data.consentGiven,
        data.otpCode
      );

      setLoadingInfo(true);
      try {
        const info = await validateSigningToken(token);
        setEnvelopeInfo(info as EnvelopeInfo);
      } catch (err) {
        logError('signatures', err, { source: 'ClientSigningPage.refreshAfterSubmit' });
      } finally {
        setLoadingInfo(false);
      }

      toast.success(
        'Signature submitted',
        'We have sent the document on for company signing. Your signed copy is ready to download below.'
      );
      setSignatureModalOpen(false);
    } catch (err: any) {
      logError('signatures', err, { source: 'ClientSigningPage.handleSubmitSignature' });
      toast.error(
        "Your signature wasn't submitted",
        err.message ||
          'Nothing was saved. Check your connection and try signing again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!token) return;

    setDownloading(true);
    try {
      const { downloadUrl } = await downloadSignedPdf(token);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      logError('signatures', err, { source: 'ClientSigningPage.handleDownload' });
      toast.error(
        "Couldn't download the document",
        err.message || 'Try again in a moment, or contact the sender.'
      );
    } finally {
      setDownloading(false);
    }
  };

  // Check if document is signed
  const isSigned = envelopeInfo && ['CLIENT_SIGNED', 'COMPANY_SIGNED', 'COMPLETED'].includes(envelopeInfo.status);

  // This page is often the only Sentral surface a signer ever sees, so the
  // waiting and failure states carry the mark too.
  if (loadingInfo || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SigningPageHeader />
        <LoadingState message="Loading your document…" />
      </div>
    );
  }

  if (error || !envelopeInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SigningPageHeader />
        <div className="mx-auto flex max-w-md px-4 py-12">
          <Card className="w-full">
            <CardContent className="pt-6">
              <div className="space-y-4 text-center">
                <div className="mx-auto w-fit rounded-full bg-error/10 p-3">
                  <AlertCircle
                    className="h-6 w-6 text-error"
                    aria-hidden="true"
                  />
                </div>
                <h1 className="text-lg font-semibold">
                  This document can't be opened
                </h1>
                <p className="text-sm text-muted-foreground">
                  {error || 'We could not open a document for this link.'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Signing links expire once they have been used. Contact whoever
                  sent it to you and ask for a fresh link.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SigningPageHeader step="Step 1 of 2 &middot; Your signature" />

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        <h1 className="sr-only">Sign document</h1>
        {/* Document Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">
                    {isSigned ? 'Signed document' : 'Document for review'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {isSigned
                      ? 'Your signature has been successfully submitted. You can download your signed copy below.'
                      : "Please review the document below. Once you're ready, click \"Sign Document\" to proceed."}
                  </p>
                </div>
                {isSigned && (
                  <CheckCircle
                    className="h-6 w-6 shrink-0 text-success"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="border rounded-lg overflow-hidden">
              <PDFPreview
                file={envelopeInfo.originalPdfUrl}
                showDownload={false}
              />
            </div>

            {/* Download Button - Only show after signing */}
            {isSigned && (
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  loading={downloading}
                >
                  {!downloading && (
                    <Download className="h-4 w-4" aria-hidden="true" />
                  )}
                  Download signed PDF
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signing Action - Sticky on mobile - Only show if not signed */}
        {!isSigned && (
          <div className="sticky bottom-0 bg-white border-t shadow-lg p-4 rounded-t-lg md:relative md:bg-transparent md:border-0 md:shadow-none md:p-0">
            <Button
              onClick={() => setSignatureModalOpen(true)}
              className="w-full md:w-auto"
              size="lg"
            >
              <FileSignature className="mr-2 h-5 w-5" />
              Sign Document
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground md:text-left">
              By signing, you agree to the terms and conditions of this document.
            </p>
          </div>
        )}
      </div>

      {/* Signature Modal */}
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSubmit={handleSubmitSignature}
        showOtp={true}
        onSendOtp={handleSendOtp}
        onVerifyOtp={handleVerifyOtp}
        otpVerified={otpVerified}
        otpLoading={otpLoading}
        loading={submitting}
        stepLabel="Step 1 of 2: Client Signing"
      />
    </div>
  );
};
